/*
 * Autosuggest component for use in service selection.  This is intended to replace the direct use of the MUI autosuggest component
 * used in several pages.
 * 
 * This takes the following props:
 *   addService            - parent function to add a service
 *   usingAccountId        - the using account id (customer)
 *   usingAccountName      - the using account name (customer scenario)
 *   provisioningAccountId - the provisioning account id (reseller)
 *   setDisableNext        - parent function to set state of next button
 *   placeholder           - if set, use this text as the placeholder for the suggestion field
 *   suggestDefaults       - if true, prepopulate with defaults.
 *   autoFocus             - if true, autoFocus on the suggestion field
 * 
 * We could also take a prop for the component style to emit, but in this iteration are only returning a table row intended
 * for use in the calling compenent's table.
 */

import React from 'react';
import Autosuggest from 'react-autosuggest';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import PropTypes from 'prop-types';
import AbEnvironment from '../auth/AbEnvironment';
import withStyles from '@mui/styles/withStyles';

import { Button, Divider, FormControl, Grid, 
         MenuItem, Paper, Select,
         TextField, Typography,
         TableRow, TableCell } from '@mui/material';
import { Search } from '@mui/icons-material';

import AppBindStyles from '../../AppBindStyles';
import CurrencyTextField from '../ui/CurrencyTextField';
import AppLogo from '../ui/AppLogo';
import SavingSpinner from '../ui/SavingSpinner';
import { getDayLabel } from '../../utils/InputUtils';
import AppFetch from '../app/AppFetch';
import AppDefaultFetch from '../app/AppDefaultFetch'

const abEnvironment = new AbEnvironment();

function renderInputComponent(inputProps) {
    const { classes, inputRef = () => {}, ref, ...other } = inputProps;
   
    return (
        <TextField
            fullWidth
            InputProps={{
                inputRef: node => {
                    ref(node);
                    inputRef(node);
                },
                classes: {
                    input: classes.input,
                },
            }}
            {...other}
        />
    );
}

function renderInputComponentAutoFocus(inputProps) {
    const { classes, inputRef = () => {}, ref, ...other } = inputProps;
   
    return (
        <TextField
            fullWidth
            autoFocus={true}
            InputProps={{
                inputRef: node => {
                    ref(node);
                    inputRef(node);
                },
                classes: {
                    input: classes.input,
                },
            }}
            {...other}
        />
    );
}

/**
 * Render a suggestion for a service name.
 * @param {*} suggestion 
 * @param {*} param1 
 * @returns 
 */
function renderServiceSuggestion(suggestion, { query, isHighlighted }) {
    const matches = match(suggestion.appName, query);
    const parts = parse(suggestion.appName, matches);
    
    // Is this template app or a vendor app?  
    // It is a template if it is owned by the reseller.
    // Need to distinguish between template and
    // new.
    let provisioningAccountId = abEnvironment.getUserAttribute('a_id');
    let isTemplate = false;
    if (suggestion.accountId === provisioningAccountId) {
        if (suggestion.appId === 'customservice') {
            isTemplate = false;
        } else {
            isTemplate = true;
        }
    }
    
    let skuPart = "";
    if (suggestion.appSku != null && suggestion.appSku !== '' && suggestion.appSku !== suggestion.appName) {
        skuPart = ' (' + suggestion.appSku + ')';
    }
   
    let appMessage = "";
    let ital = false;
    if (isTemplate) {
        let formattedPrice = Number(suggestion.appSkuPrice).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
        if (suggestion.billingFrequency === 0) {
            appMessage = "One-time fee (" + formattedPrice + ")";
        } else if (suggestion.billingFrequency === 1) {
            appMessage = "Monthly fee (" + formattedPrice + ")";
        } else if (suggestion.billingFrequency === 2) {
            appMessage = "Annual fee (" + formattedPrice + ")";
        }
    } else {
        ital = true;
        appMessage = "+ Add " + suggestion.appName;
    }
   
    return (
        <MenuItem selected={isHighlighted} component="div">
            <Grid container spacing={2} direction="row" justifyContent="flex-start">
                <Grid item xs={1}>
                    <AppLogo logoPath={suggestion.appLogo} appName={suggestion.appName} imageHeight='24px'/>
                </Grid>
                <Grid item xs={6}>
                    {/* We set the search on appname above - so that is what
                        is in the part variable. */}
                    <Typography variant="subtitle1">
                        {parts.map((part, index) => {
                            if (part.highlight && suggestion.appLogo != null) {
                                return (<strong key={index}>{part.text}</strong>);
                            } else {
                                return (part.text);
                            }
                        })}
                        {skuPart}
                    </Typography>
                </Grid>
                <Grid item xs={5}>
                    {ital &&
                    <Typography variant="subtitle1">
                        <i>{appMessage}</i>
                    </Typography>}
                    {! ital &&
                    <Typography variant="subtitle1">
                        {appMessage}
                    </Typography>}
                </Grid>
            </Grid>
        </MenuItem>
    );
}

const getSuggestionValue = suggestion => suggestion.appName;

//these two needed to make warnings go away in autoselect.  Should not be the case according to doc.
function getSectionSuggestions(section) {
    return null;
}
   
function renderSectionTitle(section) {
    return null;
}
   
const styleSheet = theme => ({
    root: {
      flexGrow: 1,
    },
    container: {
      position: 'relative',
    },
    suggestionsContainerOpen: {
      position: 'absolute',
      zIndex: 1,
      marginTop: theme.spacing(1),
      left: 0,
      right: 0,
    },
    suggestion: {
      display: 'block',
    },
    suggestionsList: {
      margin: 0,
      padding: 0,
      listStyleType: 'none',
    },
    appRow: {
      paddingTop: 0
    },
    appCellLeft: {
      paddingLeft: theme.spacing(2),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      width: 48,
      border: 0,
    },
    appCell: {
        fontSize: 14,
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        minWidth: 64,
        border: 0,
    },
    appCellPaperContainer: {
        paddingTop: 0,
        padding: 0,
        border: 0,
    },
    appCellPaper: {
        padding: theme.spacing(2),
        borderTopRightRadius: "0px",
        borderTopLeftRadius: "0px",
        borderBottomRightRadius: "0px",
        borderBottomLeftRadius: "0px",
    },
    appDetailCell: {
        fontSize: 14,
        paddingTop: 0,
        paddingBottom: theme.spacing(1),
        minWidth: 64,
        border: 0,
    },
    appCellRight: {
        fontSize: 14,
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        width: 64,
        border: 0,
    },
    appTextField: {
        marginTop: theme.spacing(1),
    },
    addButton: {
        marginTop: theme.spacing(1),
        marginRight: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },
    cancelButton: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    addFields: {
        width: 184,
    },
    markupDescription: {
      marginTop: -4, // Used to create narrower vertical spacing between title and description
    },
    textFieldNotes: {
      margin: 'theme.spacing(2)',
    }
});
  
class ServiceAutosuggest extends React.Component {
    state = {
        showSpinner: false,

        suggestions: [],
        searchP: '',
        appRowHeight: 40,

        cApp: {
            appId: null,
            accountId: null,
            appStatus: null,
            parentAppId: null,
            oemAppId: null,
            appName: null,
            appSku: null,
            appLogo: null,
            description: null,
            appType: null,
            atId: null,
            appSkuPrice: null,
            paymentOrder: 0,
            passThroughFees: null,
            billingFrequency: null,
            billingDayOfMonth: null,
            notes: null,
          },        
    };

    /**
     * If a default app is passed in, set the state to that app.
     */
    componentDidMount() {
        if (this.props.app) {
            let theApp = this.props.app;

            let appName = theApp.appName;
            if (appName.length > 64) {
              appName = appName.substring(0, 64)
            }
        
            // Setup the new capp...
            let paymentOrder = theApp.paymentOrder;
            if (paymentOrder === 1) {
              paymentOrder = 0;
            }
            if (paymentOrder == null) {
              paymentOrder = 0;
            }
          
            let notesval = theApp.notes;
            if (notesval == null)
              notesval = '';
        
            let nobj = {
              appId: theApp.appId,
              accountId: theApp.accountId,
              appStatus: theApp.appStatus,
              parentAppId: theApp.parentAppId,
              oemAppId: theApp.oemAppId,
              appName: appName,
              appSku: theApp.appSku,
              appLogo: theApp.appLogo,
              description: theApp.description,
              appType: theApp.appType,
              atId: theApp.atId,
              appSkuPrice: theApp.appSkuPrice,
              paymentOrder: paymentOrder,
              passThroughFees: theApp.passThroughFees,
              billingFrequency: theApp.billingFrequency,
              billingDayOfMonth: theApp.billingDayOfMonth,
              notes: notesval
            };
        
            this.setState({cApp: nobj});
        }
    }

    /**
     * Function for adding a service.  This calls the parent prop addService
     * function, passing the cApp state.
     */
    addService = () => {
        this.setState({showSpinner: true});
        this.props.addService(this.state.cApp);
    }

    /**
     * Function for cancelling adding a subscription. 
     */
    cancelAddService = () => {
        this.setState({
            searchP: '',
            cApp: {
                appId: null,
                accountId: null,
                appStatus: null,
                parentAppId: null,
                oemAppId: null,
                appName: null,
                appSku: null,
                appLogo: null,
                description: null,
                appType: null,
                atId: null,
                appSkuPrice: null,
                paymentOrder: 0,
                passThroughFees: null,
                billingFrequency: null,
                billingDayOfMonth: null,
                notes: null,
              },        
        });

        if (this.props.setDisableNext) {
            this.props.setDisableNext(false);
        }
    }

    /**
     * Sort the list of services to be displayed.
     * Unlike apps, services are only at the reseller level - there
     * are no vendor apps.
     * 
     * @param {*} provisioningAccountId 
     * @param {*} searchParm 
     * @param {*} thenodes 
     */
    sortServiceList(provisioningAccountId, searchParm, thenodes) {
        let theApps = [];
        let templateApps = [];
        let adx = 0;
        let exactMatch = false;
    
        // Instead of foreach, loop through using an index so that
        // we can break after showing a set number of choices.
        for (let i=0; i<thenodes.length; i++)
        {
            let n = thenodes[i];
            // If it is a template app, add it.
            if (n.accountId === provisioningAccountId) {
                if (n.appName === this.state.searchP) {
                    exactMatch = true;
                }
                templateApps.push(n);
            }
        }

        // Sort the services by name
        templateApps.sort(function(a, b) {
            a = a.appName;
            b = b.appName;
            return a>b ? 1 : a<b ? -1: 0; 
        });
    
        // Add whatever they are typing if non-blank and it does not exactly match another app
        if (this.state.searchP !== '' && !exactMatch) {
            let custApp = {
                appId: 'customservice', // Set a default app id
                accountId: provisioningAccountId,
                appStatus: 0,
                parentAppId: null,
                oemAppId: null,
                appName: searchParm,
                appSku: '', // Set a default sku
                appLogo: null,
                description: '', //Set a default description
                appType: 1,
                atId: 'f5c70f86-135a-11e8-bb45-97ef56920c02',
                appSkuPrice: 100.00,
                paymentOrder: 0,
                passThroughFees: 0,
                billingFrequency: 0,
                billingDayOfMonth: 0,
                notes: '', 
            };
            theApps.push(custApp);
        }
    
        // Put top 10 into output array
        for (let i=0; i<templateApps.length; i++)
        {
            theApps.push(templateApps[i]);
            adx++;
            if (adx >= 10)
                break;
        }

        this.setSuggestionState(theApps);
    }

    /*
     * Lookup apps for selection.  The lookup will return both template and vendor apps.  Unlike
     * usage in the app catalog, don't show vendor apps if they appear in a template.
     */
    fetchApps = (searchParm) => {
        // Provisioning account id
        let provisioningAccountId = this.props.provisioningAccountId;

        if (!searchParm || searchParm === '') {
            if (this.props.suggestDefaults === true) {
                AppDefaultFetch.fetch(provisioningAccountId, 1).toPromise()
                .then(adf => {
                    this.sortServiceList (provisioningAccountId, searchParm, adf.appsearchsuggestedapps2.nodes);
                })
                .catch(err => {
                    this.setSuggestionState([]);
                });
            } else {
                this.setSuggestionState([]);
            }
        } else {
            AppFetch.fetch(provisioningAccountId, searchParm, 1, 50).toPromise()
            .then(af => {
                this.sortServiceList (provisioningAccountId, searchParm, af.appsearchallavailablebyappname2.nodes);
            })
            .catch(err => {
                this.setSuggestionState([]);
            })
        }
    }
    
    handleSuggestionEntry = name => event => {
        if (name === 'searchP') {
            this.setState({searchP: event.target.value});
        } 
    };

    setSuggestionState = (suggestions) => {
        // Calculate height based on number of suggestions
        let height = 40 + (suggestions.length * 36);
        this.setState({suggestions: suggestions, appRowHeight: height});
    }

    setStateFromSelection(theApp) {
        //do not allow an appname > 64!
        let appName = theApp.appName;
        if (appName.length > 64) {
            appName = appName.substring(0, 64)
        }
        
        // Customer pay always
        let paymentOrder = 0;

        let notesval = theApp.notes;
        if (notesval == null)
            notesval = '';
        
        // never pass through fees
        let passThroughFees = 0;

        let nobj = {
            appId: theApp.appId,
            accountId: theApp.accountId,
            appStatus: theApp.appStatus,
            parentAppId: theApp.parentAppId,
            oemAppId: theApp.oemAppId,
            appName: appName,
            appSku: theApp.appSku,
            appLogo: theApp.appLogo,
            description: theApp.description,
            appType: theApp.appType,
            atId: theApp.atId,
            appSkuPrice: theApp.appSkuPrice,
            paymentOrder: paymentOrder,
            passThroughFees: passThroughFees,
            billingFrequency: theApp.billingFrequency,
            billingDayOfMonth: 0,
            notes: notesval,
        };
        
        this.setState({cApp: nobj});
        
        if (this.props.setDisableNext) {
            this.props.setDisableNext(true);
        }
    }

    /**
     * Set a state attribute for the service.  Note that in
     * state, we say "cApp" instead of "cService".
     * @param {*} key 
     * @param {*} value 
     */
    setServiceStateAttribute = (key, value) => {
        let tService = this.state.cApp;
        tService[key] = value;
        this.setState({cApp: tService});
    }

    onSuggestionsFetchRequested = ({ value }) => {
        this.fetchApps(value);
    }
        
    onSuggestionsClearRequested = () => {
        this.setSuggestionState([]);
    }
    
    onSuggestionSelected = (event, { suggestion, suggestionValue, suggestionIndex, sectionIndex, method }) =>{
        this.setStateFromSelection(suggestion);
    }

    shouldAutoRender = (value) => {
        return true;
    }

    getErrorMessage(field, minValue, maxValue) {
        let errMesg = null;
    
        let value = Number(field); 
        if (value < minValue) {
            return 'Cannot be less than ' + minValue;
        }
        if (value > maxValue) {
            return 'Cannot be greater than ' + maxValue;
        }
    
        return errMesg;
    }
    
    areFieldsInError() {
        const { getMaxServiceAmount } = this.props.auth;

        if (this.getErrorMessage(this.state.cApp.appSkuPrice, 1, getMaxServiceAmount()) !== null) {
          return true;
        }
    
        if (this.state.cApp.billingFrequency === 1 && this.state.cApp.billingDayOfMonth === null) {
          return true;
        }
    
        return false;
    }

    getBillingDayLabel(billingDayOfMonth) {
        if (billingDayOfMonth === 0 || billingDayOfMonth === null) { 
            return 'Billed on day after order accepted'; 
        } 
        if (billingDayOfMonth === 32) { 
            return 'Billed on last day of month'; 
        } 
    
        return 'Billed on ' + getDayLabel(billingDayOfMonth) + ' of the month';
    }    
        
    render() {
        const classes = this.props.classes;
        const { getMaxServiceAmount } = this.props.auth;


        let showDetails = true;
        let showCustom = false;

        if (this.state.cApp.appId == null) {
            showDetails = false;
        } else if (this.state.cApp.appId === 'customservice') {
            showCustom = true;
        }

        let inpValue = this.state.searchP;
        if (inpValue == null)
            inpValue = '';

        let placeholder = 'Add a service you provide to the project';
        if (this.props.placeholder) {
            placeholder = this.props.placeholder;
        }

        if (showDetails === false) {
            return (
                <TableRow>
                    <TableCell colSpan={6} style={{verticalAlign: 'top', height: this.state.appRowHeight, border: 0}}>
                        {this.props.autoFocus &&
                        <Autosuggest
                            renderInputComponent={renderInputComponentAutoFocus}
                            suggestions={this.state.suggestions}
                            onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                            onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                            onSuggestionSelected={this.onSuggestionSelected}
                            getSuggestionValue={getSuggestionValue}
                            renderSuggestion={renderServiceSuggestion}
                            shouldRenderSuggestions={this.shouldAutoRender}
                            multiSection={false}
                            renderSectionTitle={renderSectionTitle}
                            getSectionSuggestions={getSectionSuggestions}
                            inputProps={{
                                classes,
                                placeholder: placeholder,
                                value: inpValue,
                                onChange: this.handleSuggestionEntry('searchP'),
                            }}
                            theme={{
                                container: classes.container,
                                suggestionsContainerOpen: classes.suggestionsContainerOpen,
                                suggestionsList: classes.suggestionsList,
                                suggestion: classes.suggestion,
                            }}
                            renderSuggestionsContainer={options => (
                                <Paper {...options.containerProps} square>
                                    {options.children}
                                </Paper>
                            )}
                        />}
                        {! this.props.autoFocus &&
                        <Autosuggest
                            renderInputComponent={renderInputComponent}
                            suggestions={this.state.suggestions}
                            onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                            onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                            onSuggestionSelected={this.onSuggestionSelected}
                            getSuggestionValue={getSuggestionValue}
                            renderSuggestion={renderServiceSuggestion}
                            shouldRenderSuggestions={this.shouldAutoRender}
                            multiSection={false}
                            renderSectionTitle={renderSectionTitle}
                            getSectionSuggestions={getSectionSuggestions}
                            inputProps={{
                                classes,
                                placeholder: placeholder,
                                value: inpValue,
                                onChange: this.handleSuggestionEntry('searchP'),
                            }}
                            theme={{
                                container: classes.container,
                                suggestionsContainerOpen: classes.suggestionsContainerOpen,
                                suggestionsList: classes.suggestionsList,
                                suggestion: classes.suggestion,
                            }}
                            renderSuggestionsContainer={options => (
                                <Paper {...options.containerProps} square>
                                    {options.children}
                                </Paper>
                            )}
                        />}
                        <Search color="disabled" style={{float: "right", marginTop: -26}}/>
                    </TableCell>
                </TableRow>
            );
        }

        //
        // Falling through here means that a service has been selected
        //

        return (
            <TableRow className={classes.appRow}>
                <TableCell className={classes.appCellPaperContainer} colSpan={6}>
                    <Paper className={classes.appCellPaper} elevation={12}>
                        <Grid container justifyContent="flex-start" alignItems="center" spacing={2}>
                            <Grid item>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item >
                                        <AppLogo logoPath={this.state.cApp.appLogo} appName={this.state.cApp.appName} imageHeight='24px'/>
                                    </Grid>
                                    <Grid item>
                                        <Typography>{this.state.cApp.appName}</Typography>
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}><Divider/></Grid>
                            <Grid item xs={12}>
                                <Grid container spacing={1} justifyContent="space-between" alignItems="flex-start">
                                    <Grid item xs={12}>
                                        <Grid container spacing={1} justifyContent="space-between" alignItems="flex-start">
                                            <Grid item>
                                                <Grid container direction="column" spacing={0}>
                                                    <Grid item>
                                                        <Typography variant="body1" style={AppBindStyles.boldLabel}>Amount</Typography>
                                                    </Grid>
                                                    <Grid item style={AppBindStyles.markupDescription}> 
                                                        <Typography variant="caption">Amount to bill for this service.</Typography>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            <Grid item>
                                                <CurrencyTextField 
                                                    id="samount" 
                                                    className={classes.addFields}
                                                    value={this.state.cApp.appSkuPrice}
                                                    allowNegative={false}
                                                    showErrMsg={true}
                                                    error={this.getErrorMessage(this.state.cApp.appSkuPrice, 1, getMaxServiceAmount())}
                                                    errorMsg={this.getErrorMessage(this.state.cApp.appSkuPrice, 1, getMaxServiceAmount())}
                                                    minValue={1}
                                                    maxValue={getMaxServiceAmount()}
                                                    minEnterValue={0}
                                                    maxEnterValue={9999999}
                                                    onChange={ (e) => {this.setServiceStateAttribute("appSkuPrice", e.target.value )} }
                                                    textAlign="right"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Grid container pacing={1} justifyContent="space-between" alignItems="flex-start">
                                            <Grid item>
                                                <Grid container direction="column" spacing={0}>
                                                    <Grid item>
                                                        <Typography variant="body1" style={AppBindStyles.boldLabel}>Frequency</Typography>
                                                    </Grid>
                                                    <Grid item style={AppBindStyles.markupDescription}> 
                                                        <Typography variant="caption">Services can be billed as a one-time fee (e.g. setup), or a monthly recurring fee.</Typography>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            <Grid item>
                                                <FormControl>
                                                    <Select
                                                        value={this.state.cApp.billingFrequency }
                                                        className={classes.addFields}
                                                        onChange={ (e) => {this.setServiceStateAttribute("billingFrequency", e.target.value )} }
                                                        inputProps={{
                                                            name: 'billingFrequency',
                                                            id: 'billingFrequency',
                                                        }}
                                                    >                                   
                                                        <MenuItem value={0}>One-time fee</MenuItem>
                                                        <MenuItem value={1}>Monthly</MenuItem>
                                                        {/*<MenuItem value={2}>Annual</MenuItem>*/}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    {this.state.cApp.billingFrequency === 1 &&
                                    <Grid item xs={12}>
                                        <Grid container spacing={1} justifyContent="space-between" alignItems="flex-start">
                                            <Grid item>
                                                <Grid container direction="column" spacing={0}>
                                                    <Grid item>
                                                        <Typography variant="body1" style={AppBindStyles.boldLabel}>{"Billing day of month"}</Typography>
                                                    </Grid>
                                                    <Grid item style={AppBindStyles.markupDescription}> 
                                                        <Typography variant="caption" >Select the day of the month that this recurring service fee will be billed</Typography>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            <Grid item > 
                                                <FormControl>
                                                    <Select
                                                        className={classes.addFields}
                                                        value={this.state.cApp.billingDayOfMonth}
                                                        onChange={ (e) => {this.setServiceStateAttribute("billingDayOfMonth", e.target.value )} }
                                                        inputProps={{
                                                        name: 'billingDayOfMonth',
                                                        id: 'billingDayOfMonth',
                                                        }}
                                                    >                                   
                                                        <MenuItem value={0}>Day after order accepted</MenuItem>
                                                        <MenuItem value={1}>1</MenuItem>
                                                        <MenuItem value={2}>2</MenuItem>
                                                        <MenuItem value={3}>3</MenuItem>
                                                        <MenuItem value={4}>4</MenuItem>
                                                        <MenuItem value={5}>5</MenuItem>
                                                        <MenuItem value={6}>6</MenuItem>
                                                        <MenuItem value={7}>7</MenuItem>
                                                        <MenuItem value={8}>8</MenuItem>
                                                        <MenuItem value={9}>9</MenuItem>
                                                        <MenuItem value={10}>10</MenuItem>
                                                        <MenuItem value={11}>11</MenuItem>
                                                        <MenuItem value={12}>12</MenuItem>
                                                        <MenuItem value={13}>13</MenuItem>
                                                        <MenuItem value={14}>14</MenuItem>
                                                        <MenuItem value={15}>15</MenuItem>
                                                        <MenuItem value={16}>16</MenuItem>
                                                        <MenuItem value={17}>17</MenuItem>
                                                        <MenuItem value={18}>18</MenuItem>
                                                        <MenuItem value={19}>19</MenuItem>
                                                        <MenuItem value={20}>20</MenuItem>
                                                        <MenuItem value={21}>21</MenuItem>
                                                        <MenuItem value={22}>22</MenuItem>
                                                        <MenuItem value={23}>23</MenuItem>
                                                        <MenuItem value={24}>24</MenuItem>
                                                        <MenuItem value={25}>25</MenuItem>
                                                        <MenuItem value={26}>26</MenuItem>
                                                        <MenuItem value={27}>27</MenuItem>
                                                        <MenuItem value={32}>Last day of month</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </Grid>}
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                {this.state.saveStatus !== 'saving' &&
                                <React.Fragment>
                                    <Button
                                        className={classes.addButton}
                                        color="primary" variant="contained"
                                        onClick={() => this.addService()}
                                        disabled={this.areFieldsInError()}
                                    >
                                        Add service
                                    </Button>
                                    <Button
                                        className={classes.cancelButton}
                                        color="primary" variant="outlined"
                                        onClick={() => this.cancelAddService()}
                                    >
                                        Cancel
                                    </Button>
                                </React.Fragment>}
                                {this.state.saveStatus === 'saving' &&
                                <Button disabled={true} color="primary" variant="contained" >
                                    <SavingSpinner/>
                                </Button>}
                            </Grid>
                        </Grid>
                    </Paper>
                </TableCell>
            </TableRow>
        );
    }
}

ServiceAutosuggest.propTypes = {
    addService: PropTypes.func.isRequired,
    usingAccountId: PropTypes.string.isRequired,
    usingAccountName: PropTypes.string.isRequired,
    provisioningAccountId: PropTypes.string.isRequired,
    setDisableNext: PropTypes.func.isRequired,
    placeholder: PropTypes.string.isRequired,
    suggestDefaults: PropTypes.bool.isRequired,
  };

export default withStyles(styleSheet)(ServiceAutosuggest);
